import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import ViewDayOutlinedIcon from '@mui/icons-material/ViewDayOutlined';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { useCppInvoice, useRefreshCppInvoice } from '~/hooks/apis/costPerPoint';

import type { CppPartner } from '~/types/costPerPoint';

import ForceRefreshDialog from './ForceRefreshDialog';
import GenerateInvoiceDialog from './GenerateInvoiceDialog';
import InvoiceNotesAlert from './InvoiceNotesAlert';
import PartnerInvoiceDocument, { INVOICE_PRINT_ID, type InvoiceFormat } from './PartnerInvoiceDocument';
import PartnerMonthFilters from './PartnerMonthFilters';
import { buildRateGroupLines, formatCurrencyCents } from './invoiceFormat';
import { type InvoiceMonth, formatMonthLabel, monthToRange } from './monthRange';
import { describeMissingBilling, resolvePartnerBilling } from './partnerBilling';
import { useExportTransactionsCsv } from './useExportTransactionsCsv';

/** Everything that can withhold invoice generation, in the order it should be reported. */
interface GenerateGate {
  /** The period's currency when it is one this document cannot bill in; null when it can. */
  unsupportedCurrency: string | null;
  /** Gap between the per-rate rows and the period total that rounding cannot explain. */
  unreconciledCents: number;
  /** Whether the service reported anything that blocks invoicing the period. */
  hasBlockers: boolean;
}

/**
 * Why invoice generation is withheld, or empty when it is allowed.
 *
 * Ordered most fundamental first: a period billed in the wrong currency cannot be fixed by
 * resolving blockers, and figures that do not add up should be raised before anything else.
 */
function resolveGenerateBlockedReason({ unsupportedCurrency, unreconciledCents, hasBlockers }: GenerateGate): string {
  if (unsupportedCurrency) {
    return `This period is priced in ${unsupportedCurrency}; this invoice can only be issued in ${SUPPORTED_CURRENCY}`;
  }
  if (unreconciledCents !== 0) {
    return 'The line items do not reconcile to the period total — resolve before generating';
  }
  if (hasBlockers) {
    return 'Resolve the blockers below before generating this invoice';
  }
  return '';
}

/** The only currency this document can legitimately bill in. */
const SUPPORTED_CURRENCY = 'AUD';

/**
 * Print rules that isolate the invoice.
 *
 * The admin portal is a single-page app, so printing without this would emit the nav, filters and
 * tabs alongside the document. Hiding everything and re-showing only the invoice subtree is the
 * least invasive way to get a clean page out of `window.print()` without restructuring the layout.
 */
const printStyles = (
  <GlobalStyles
    styles={{
      // Zero page margins suppress the browser's own header and footer — the date, page title,
      // source URL and page number it would otherwise stamp on every sheet, none of which belong
      // on an invoice sent to a partner. The margin they occupied is given back as padding on the
      // document itself, so the content is not printed to the paper's edge.
      '@page': { size: 'auto', margin: 0 },
      '@media print': {
        'body *': { visibility: 'hidden' },
        [`#${INVOICE_PRINT_ID}, #${INVOICE_PRINT_ID} *`]: { visibility: 'visible' },
        [`#${INVOICE_PRINT_ID}`]: {
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          padding: '14mm',
          border: 'none',
          boxShadow: 'none',
        },
        // A long period runs to several sheets, so the line-item header repeats on each and no
        // single row is split across a page break.
        [`#${INVOICE_PRINT_ID} thead`]: { display: 'table-header-group' },
        [`#${INVOICE_PRINT_ID} tr`]: { breakInside: 'avoid' },
      },
    }}
  />
);

interface Props {
  partners: Array<CppPartner>;
  hasPartnersError: boolean;
  partnerId: string;
  month: InvoiceMonth;
  onPartnerChange: (partnerId: string) => void;
  onMonthChange: (month: InvoiceMonth) => void;
}

/**
 * Generates and displays a partner's invoice for one calendar month.
 *
 * Viewing runs the endpoint in `preview` mode, which prices any transaction that had no cost yet
 * and leaves the rest alone. The service decides whether the period can be invoiced at all: while
 * `blockers` is non-empty it persists nothing, so the document is still rendered — an admin needs
 * to see the figures to work out what is missing — but saving is withheld and the blockers are
 * shown with the detail needed to fix them.
 */
export default function PartnerInvoicePage({
  partners,
  hasPartnersError,
  partnerId,
  month,
  onPartnerChange,
  onMonthChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isRefreshDialogOpen, setIsRefreshDialogOpen] = useState(false);
  // Presentation only — both formats bill the same money, so this never touches what is charged.
  const [format, setFormat] = useState<InvoiceFormat>('daily');
  // A month of mostly-idle days buries the ones being billed; on by default for that reason.
  const [hideEmptyDays, setHideEmptyDays] = useState(true);
  const { exportCsv, cancel: cancelExport, progress: exportProgress, isExporting } = useExportTransactionsCsv();

  const range = useMemo(() => monthToRange(month), [month]);
  const { data, isLoading, isError, error } = useCppInvoice({ partnerId, ...range });
  const refreshMutation = useRefreshCppInvoice();

  const partner = useMemo(() => partners.find((candidate) => candidate.id === partnerId), [partners, partnerId]);
  const partnerName = partner?.name ?? '';
  const billing = useMemo(() => resolvePartnerBilling(partner), [partner]);
  const missingBillingWarning = useMemo(() => describeMissingBilling(billing.missing), [billing.missing]);
  // Supplied when generating, since nothing here mints or reserves invoice numbers. Cleared
  // whenever the partner or month changes: these belong to one partner's one period, and carrying
  // them across would stamp them on the wrong document.
  const [issued, setIssued] = useState<{ invoiceNumber: string; invoiceDate: string; dueDate: string }>({
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
  });
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  // Printing has to wait for the document to re-render with the details just entered, so the
  // confirm handler sets this and an effect prints once React has committed the new values.
  const [isPrintPending, setIsPrintPending] = useState(false);

  useEffect(() => {
    setIssued({ invoiceNumber: '', invoiceDate: '', dueDate: '' });
  }, [partnerId, month]);

  useEffect(() => {
    if (isPrintPending && issued.invoiceNumber) {
      setIsPrintPending(false);
      window.print();
    }
  }, [isPrintPending, issued.invoiceNumber]);

  // An export belongs to the selection that started it, so moving off that selection abandons the
  // walk instead of leaving it fetching pages for a period no longer on screen.
  useEffect(() => cancelExport, [cancelExport, partnerId, month]);

  const handleGenerate = (details: { invoiceNumber: string; invoiceDate: string; dueDate: string }) => {
    setIssued(details);
    setIsGenerateDialogOpen(false);
    setIsPrintPending(true);
  };

  const invoice = data?.result;
  const blockers = invoice?.blockers ?? [];
  const hasBlockers = blockers.length > 0;

  /**
   * The document, the remittance account and the GST default are all Australian, so a period priced
   * in another currency would render a foreign total billed into an AUD account with Australian GST
   * on it — plausible-looking and wrong. Refuse it rather than print it.
   */
  const unsupportedCurrency = invoice?.currency && invoice.currency !== SUPPORTED_CURRENCY ? invoice.currency : null;

  /**
   * Gap between the per-rate rows and the period total that rounding cannot explain. Evaluated
   * whichever format is on screen: if the figures cannot be presented two ways consistently, the
   * invoice is not safe to issue in either.
   */
  const unreconciledCents = useMemo(() => (invoice ? buildRateGroupLines(invoice).unreconciledCents : 0), [invoice]);

  /** Why generating is withheld, or empty when it is allowed. */
  const generateBlockedReason = resolveGenerateBlockedReason({ unsupportedCurrency, unreconciledCents, hasBlockers });

  const handleConfirmRefresh = async () => {
    try {
      const response = await refreshMutation.mutateAsync({ partnerId, ...range });
      setIsRefreshDialogOpen(false);

      if (response.result.blockers.length > 0) {
        enqueueSnackbar('Costs recalculated, but the period still has blockers and cannot be saved', {
          variant: 'warning',
        });
      } else {
        enqueueSnackbar(`Recalculated costs for ${formatMonthLabel(month)}`, { variant: 'success' });
      }
    } catch (refreshError) {
      enqueueSnackbar(
        `Could not recalculate costs: ${refreshError instanceof Error ? refreshError.message : 'unknown error'}`,
        { variant: 'error' },
      );
    }
  };

  return (
    <Stack spacing={3}>
      {printStyles}

      <PartnerMonthFilters
        partners={partners}
        partnerId={partnerId}
        month={month}
        onPartnerChange={onPartnerChange}
        onMonthChange={onMonthChange}
        hasPartnersError={hasPartnersError}
        actions={
          <>
            {/*
              Ordered by how the month-end cycle is worked: generate, take the backing detail, and
              only then the corrective action. Force refresh sits furthest right because it rewrites
              stored costs and is the one thing here you reach for rarely.
            */}
            <Tooltip title={generateBlockedReason}>
              {/* A disabled button fires no events, so the tooltip needs a wrapper to hang off. */}
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={() => setIsGenerateDialogOpen(true)}
                  disabled={!invoice || Boolean(generateBlockedReason)}
                >
                  Generate invoice
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="text"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={() => exportCsv({ partnerId, partnerName, range })}
              disabled={!invoice || isExporting}
            >
              {isExporting ? 'Downloading…' : 'Download transactions'}
            </Button>

            <Button
              variant="text"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={() => setIsRefreshDialogOpen(true)}
              disabled={!invoice || refreshMutation.isPending}
            >
              Force refresh
            </Button>
          </>
        }
      />

      {!partnerId && (
        <Alert severity="info" icon={<ReceiptLongIcon />}>
          Select a partner and month to generate an invoice.
        </Alert>
      )}

      {exportProgress && <Alert severity="info">{exportProgress}</Alert>}

      {isError && (
        <Alert severity="error">
          Could not generate the invoice: {error instanceof Error ? error.message : 'unknown error'}
        </Alert>
      )}

      {isLoading && partnerId && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {unsupportedCurrency && (
        <Alert severity="error">
          <AlertTitle>This period is priced in {unsupportedCurrency}</AlertTitle>
          The invoice document, the account it directs payment into, and the default GST rate are all Australian.
          Issuing it for a {unsupportedCurrency} period would bill a foreign total into an AUD account with Australian
          GST applied, so generation is blocked until that is supported properly.
        </Alert>
      )}

      {unreconciledCents !== 0 && (
        <Alert severity="error">
          <AlertTitle>The line items do not add up to the period total</AlertTitle>
          Grouping the priced days by rate leaves {formatCurrencyCents(
            unreconciledCents,
            invoice?.currency ?? null,
          )}{' '}
          unaccounted for — more than per-day rounding can explain, so it is not shown as a rounding adjustment. The
          period&apos;s daily figures and its total disagree; try a force refresh, and raise it before issuing anything
          if it persists.
        </Alert>
      )}

      {invoice && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Invoice format
            </Typography>
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={format}
              // A null value means the active button was clicked again; keep the current format
              // rather than leaving the invoice with no line items at all.
              onChange={(_event, next: InvoiceFormat | null) => next && setFormat(next)}
              aria-label="Invoice format"
              sx={{
                // The default outlined toggle reads as static text until you hover it. Giving the
                // selected option a filled swatch and the pair a visible hover state makes it
                // obvious these are two choices rather than a heading above the table.
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  px: 2,
                  py: 1.25,
                  minWidth: 220,
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                  '& .MuiTypography-caption': { color: 'primary.contrastText', opacity: 0.85 },
                },
              }}
            >
              <ToggleButton value="daily">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <ViewDayOutlinedIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Daily summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      One row per day of the period
                    </Typography>
                  </Box>
                </Stack>
              </ToggleButton>
              <ToggleButton value="rate-group">
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <SummarizeOutlinedIcon fontSize="small" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Aggregated by rate
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      One row per CPP tier applied
                    </Typography>
                  </Box>
                </Stack>
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Only meaningful for the daily rows; the aggregated format has no per-day lines. */}
            {format === 'daily' && (
              <FormControlLabel
                sx={{ display: 'flex', mt: 1.5 }}
                control={
                  <Checkbox checked={hideEmptyDays} onChange={(event) => setHideEmptyDays(event.target.checked)} />
                }
                label={
                  <Box>
                    <Typography variant="body2">Hide days with no activity</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Drops days that billed nothing and moved no points. Unpriced days always stay.
                    </Typography>
                  </Box>
                }
              />
            )}
          </Paper>

          <InvoiceNotesAlert
            notes={blockers}
            severity="error"
            currency={invoice.currency}
            title={`${formatMonthLabel(month)} cannot be saved yet — resolve the following first`}
          />
          <InvoiceNotesAlert
            notes={invoice.warnings}
            severity="warning"
            currency={invoice.currency}
            title="Check before sending"
          />

          {missingBillingWarning && <Alert severity="warning">{missingBillingWarning}</Alert>}

          <PartnerInvoiceDocument
            invoice={invoice}
            billing={billing}
            month={month}
            invoiceNumber={issued.invoiceNumber}
            invoiceDate={issued.invoiceDate}
            hideEmptyDays={hideEmptyDays}
            dueDate={issued.dueDate}
            format={format}
          />
        </>
      )}

      <GenerateInvoiceDialog
        open={isGenerateDialogOpen}
        month={month}
        partnerName={partnerName}
        onCancel={() => setIsGenerateDialogOpen(false)}
        onConfirm={handleGenerate}
      />

      <ForceRefreshDialog
        open={isRefreshDialogOpen}
        partnerName={partnerName}
        month={month}
        isRefreshing={refreshMutation.isPending}
        onConfirm={handleConfirmRefresh}
        onCancel={() => setIsRefreshDialogOpen(false)}
      />
    </Stack>
  );
}
